import { useEffect, useRef, useState } from "react";
import { Loader2, MapPin } from "lucide-react";
import { Input } from "./ui/input";

// ─── Module-level cache (shared across all instances) ─────────────────
// Caches the parsed-and-filtered India suggestion list keyed by normalized
// query string. Entries expire after CACHE_TTL_MS so stale OSM data doesn't
// linger forever. A small LRU cap keeps memory bounded.
const SEARCH_CACHE = new Map();
const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes
const CACHE_MAX_ENTRIES = 200;

function readCache(key) {
  const hit = SEARCH_CACHE.get(key);
  if (!hit) return null;
  if (Date.now() - hit.t > CACHE_TTL_MS) {
    SEARCH_CACHE.delete(key);
    return null;
  }
  // refresh LRU order
  SEARCH_CACHE.delete(key);
  SEARCH_CACHE.set(key, hit);
  return hit.v;
}

function writeCache(key, value) {
  if (SEARCH_CACHE.size >= CACHE_MAX_ENTRIES) {
    const oldest = SEARCH_CACHE.keys().next().value;
    if (oldest) SEARCH_CACHE.delete(oldest);
  }
  SEARCH_CACHE.set(key, { v: value, t: Date.now() });
}

const cacheKey = (q) => `india|${q.toLowerCase().trim()}`;

/**
 * India-only address autocomplete using free Nominatim (OpenStreetMap).
 *
 * - countrycodes=in restricts the search to India
 * - debounced 600ms to respect Nominatim's 1 req/sec usage policy
 * - frontend re-filters results so only India entries are shown
 * - selecting a suggestion emits {address, lat, lng, osm_id, place_id}
 * - manual typing is always allowed; caller validates India on submit
 * - results cached in-memory for 15 min per normalized query
 * - in-flight request is cancelled (AbortController) if user keeps typing
 * - on Nominatim 429 / service error, shows a friendly fallback message
 *   while keeping the manual-typing input fully usable
 */
export default function IndiaAddressAutocomplete({
  value,
  onChange,
  onSelectLocation,
  placeholder,
  className = "",
  required = false,
  ...inputProps
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  const skipNextSearchRef = useRef(false);
  const abortRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Abort any pending request on unmount.
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Debounced Nominatim search with 15-min in-memory cache + abort-on-retype
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (skipNextSearchRef.current) {
      // value just changed because the user clicked a suggestion — don't re-search
      skipNextSearchRef.current = false;
      return;
    }
    const q = (value || "").trim();
    if (q.length < 3) {
      setSuggestions([]);
      setError(null);
      // Cancel any in-flight request when query becomes too short
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      return;
    }

    // Cache hit → render instantly, skip network entirely
    const ckey = cacheKey(q);
    const cached = readCache(ckey);
    if (cached) {
      setSuggestions(cached.suggestions);
      setError(cached.error || null);
      setOpen(true);
      setLoading(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      // Cancel any earlier in-flight request before starting a new one.
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        setLoading(true);
        setError(null);
        // Free Nominatim (OpenStreetMap) — exact params per spec.
        // countrycodes=in restricts results to India at the source.
        const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&addressdetails=1&limit=5&countrycodes=in`;
        const res = await fetch(url, { headers: { Accept: "application/json" }, signal: controller.signal });
        if (res.status === 429) {
          throw new Error("rate-limited");
        }
        if (!res.ok) throw new Error(`Lookup failed (${res.status})`);
        const data = await res.json();
        // Defensive double-filter — only India results.
        const indiaOnly = (Array.isArray(data) ? data : []).filter((d) => {
          const cc = (d?.address?.country_code || "").toLowerCase();
          const cn = (d?.address?.country || "").toLowerCase();
          return cc === "in" || cn === "india";
        }).slice(0, 5);
        // Map to the uniform shape the renderer + handlePick already expect.
        const mapped = indiaOnly.map((d) => ({
          place_id: d.osm_id || d.place_id,
          osm_id: d.osm_id,
          display_name: d.display_name,
          lat: String(d.lat ?? ""),
          lon: String(d.lon ?? ""),
          country_code: d.address?.country_code,
        }));
        const rejectedNonIndia = (data?.length ?? 0) > 0 && indiaOnly.length === 0;
        const errMsg = rejectedNonIndia ? "We currently support deliveries within India only." : null;
        // Cache the parsed result so a re-typed query renders instantly.
        writeCache(ckey, { suggestions: mapped, error: errMsg });
        setSuggestions(mapped);
        setError(errMsg);
        setOpen(true);
      } catch (err) {
        if (err?.name === "AbortError") return; // newer query took over — silently drop
        if (err?.message === "rate-limited") {
          // Friendly message per spec — manual typing remains usable.
          setError("Address suggestions are temporarily unavailable. You can still type the address manually.");
        } else {
          setError("Couldn't reach the address service. Please type your address manually.");
        }
        setSuggestions([]);
        setOpen(true);
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setLoading(false);
      }
    }, 600);

    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value]);

  const handlePick = (s) => {
    skipNextSearchRef.current = true;
    onChange?.(s.display_name);
    onSelectLocation?.({
      address: s.display_name,
      lat: parseFloat(s.lat),
      lng: parseFloat(s.lon),
      osm_id: s.osm_id || s.place_id,
      place_id: s.place_id,
    });
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <Input
        value={value || ""}
        onChange={(e) => {
          onChange?.(e.target.value);
          // Re-open dropdown if user starts typing again
          if (e.target.value && !open) setOpen(true);
        }}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        className={className}
        required={required}
        autoComplete="off"
        {...inputProps}
      />
      {loading && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400 pointer-events-none" />
      )}
      {open && (suggestions.length > 0 || error) && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-xl border border-slate-200 shadow-lg max-h-64 overflow-y-auto" data-testid="address-suggestion-list">
          {error && (
            <div className="px-3 py-2 text-xs text-rose-600 bg-rose-50 border-b border-rose-100" data-testid="address-india-only-error">
              {error}
            </div>
          )}
          {suggestions.map((s) => (
            <button
              key={s.place_id}
              type="button"
              onClick={() => handlePick(s)}
              className="w-full text-left px-3 py-2 hover:bg-orange-50 transition-colors flex items-start gap-2 border-b border-slate-50 last:border-0"
              data-testid={`address-suggestion-${s.place_id}`}
            >
              <MapPin className="h-3.5 w-3.5 text-[#FF6B00] mt-0.5 flex-shrink-0" />
              <span className="text-xs text-slate-700 leading-snug line-clamp-2">{s.display_name}</span>
            </button>
          ))}
          {/* Manual-entry hint — always shown so customer knows free-text is allowed
              when the exact place (e.g. a hostel, gate, or unnamed lane) isn't in
              OSM. Sticky bottom so it stays visible even when the suggestion list
              is long enough to scroll. No behaviour change: typing was always
              allowed; this just makes the affordance discoverable. */}
          <div
            className="sticky bottom-0 px-3 py-2 text-[11px] text-slate-600 bg-amber-50 border-t border-amber-100 leading-snug"
            data-testid="address-manual-entry-hint"
          >
            <span className="font-medium text-amber-700">Can&apos;t find your location?</span>{" "}
            Just type the full address and continue — we&apos;ll use what you entered.
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Server-side India check. Used as the manual-typing fallback before booking.
 * Returns true if the address geocodes to a place inside India (or empty).
 * Uses Nominatim with countrycodes=in.
 */
export async function isIndiaAddress(address) {
  const q = (address || "").trim();
  if (!q) return true;
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&addressdetails=1&limit=1&countrycodes=in`,
      { headers: { Accept: "application/json" } }
    );
    if (!res.ok) return true;  // network blip → don't block
    const data = await res.json();
    if (!Array.isArray(data) || data.length === 0) {
      // fall back to permissive heuristic: contains "India" or 6-digit PIN code
      const lower = q.toLowerCase();
      return lower.includes("india") || /\b\d{6}\b/.test(q);
    }
    const cc = (data[0]?.address?.country_code || "").toLowerCase();
    return cc === "in";
  } catch {
    return true; // network failure → permit (UX over strictness)
  }
}
